// Визуальные эффекты: FLIP-анимации перестановки карт, полёт клонов, всплывающие числа, встряска, заставки.
import type { GameEvent, PlayerId } from '../types.ts';
import { cardOrNull, DB } from '../engine/state.ts';
import { sfx } from './sound.ts';
import { esc, cardHtml } from './cards.ts';
import { notify } from './notify.ts';

/** Уведомление в ленте: подпись и, если есть, карта */
function notice(caption: string, cls: string, cid: string | null | undefined = null, { hold, priority = 'high' as const }: { hold?: number; priority?: 'low' | 'high' } = {}): void {
  const d = cardOrNull(cid);
  const cardPart = d ? cardHtml({ cid: d.id }, { cls: 'sm', zone: 'view', key: null }) : '';
  notify({ html: `<div class="n-cap">${caption}</div>${cardPart}`, cls, priority, ...(hold != null ? { hold } : {}) });
}

const layer = (): HTMLElement | null => document.getElementById('fx');
const reduce = (): boolean => !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const EASE = 'cubic-bezier(.2,.8,.2,1)';

export interface Snapshot {
  rect: DOMRect;
  el: HTMLElement;
  /** outerHTML карты — чтобы полететь клоном, когда элемента уже нет */
  html: string | null;
}
export type Snap = Map<string, Snapshot>;

type RectLike = Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>;
type Target = Element | RectLike | null | undefined;

function rectOf(t: Target): RectLike | null {
  if (!t) return null;
  return t instanceof Element ? t.getBoundingClientRect() : t;
}

// ---------- снимок позиций ----------
export function capture(root: ParentNode | null): Snap {
  const m: Snap = new Map();
  if (!root) return m;
  for (const el of root.querySelectorAll<HTMLElement>('[data-fx]')) {
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    const key = el.dataset['fx'];
    if (!key) continue;
    m.set(key, { rect: r, el, html: el.classList.contains('card') ? el.outerHTML : null });
  }
  return m;
}

function center(r: RectLike): { x: number; y: number } {
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

/** FLIP: элементы с тем же ключом, сменившие место/размер, плавно переезжают. */
export function flip(before: Snap, after: Snap, { duration = 420 } = {}): void {
  if (reduce()) return;
  for (const [key, a] of after) {
    const b = before.get(key);
    if (!b) continue;
    const br = b.rect;
    const ar = a.rect;
    const c0 = center(br);
    const c1 = center(ar);
    const dx = c0.x - c1.x;
    const dy = c0.y - c1.y;
    const sx = ar.width ? br.width / ar.width : 1;
    const sy = ar.height ? br.height / ar.height : 1;
    if (Math.abs(dx) < 2 && Math.abs(dy) < 2 && Math.abs(sx - 1) < 0.03 && Math.abs(sy - 1) < 0.03) continue;
    const el = a.el;
    el.style.willChange = 'transform';
    el.animate([
      { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, boxShadow: '0 12px 30px #000c' },
      { transform: 'translate(0,0) scale(1,1)', boxShadow: '0 0 0 #0000' },
    ], { duration, easing: EASE, composite: 'add' }).onfinish = () => { el.style.willChange = ''; };
    el.style.zIndex = '30';
    setTimeout(() => { el.style.zIndex = ''; }, duration);
  }
}

/** Полёт клона карты из прямоугольника в элемент-цель (или прямоугольник). Возвращает длительность. */
export function flyClone(html: string | null, fromRect: RectLike, to: Target, { duration = 520, scaleEnd = 0.25, delay = 0, fade = true } = {}): number {
  const L = layer();
  if (!L || !html) return 0;
  const toRect = rectOf(to);
  const wrap = document.createElement('div');
  wrap.className = 'fx-fly';
  wrap.innerHTML = html;
  const el = wrap.firstElementChild;
  if (el instanceof HTMLElement) {
    el.removeAttribute('data-fx');
    el.style.margin = '0';
  }
  Object.assign(wrap.style, { position: 'fixed', left: fromRect.left + 'px', top: fromRect.top + 'px', width: fromRect.width + 'px', height: fromRect.height + 'px', zIndex: '200', pointerEvents: 'none', opacity: '0' });
  L.appendChild(wrap);
  const c0 = center(fromRect);
  const c1 = toRect ? center(toRect) : c0;
  const anim = wrap.animate([
    { transform: 'translate(0,0) scale(1)', opacity: 1, offset: 0 },
    { transform: `translate(${(c1.x - c0.x) * 0.5}px, ${(c1.y - c0.y) * 0.5 - 40}px) scale(${(1 + scaleEnd) / 2})`, opacity: 1, offset: 0.55 },
    { transform: `translate(${c1.x - c0.x}px, ${c1.y - c0.y}px) scale(${scaleEnd})`, opacity: fade ? 0 : 1 },
  ], { duration: reduce() ? 1 : duration, delay, easing: EASE, fill: 'forwards' });
  anim.onfinish = () => wrap.remove();
  return duration + delay;
}

/** Карта «прилетает» в элемент из точки (появление): анимируем сам элемент. */
export function arrive(el: HTMLElement | null, fromEl: Target, { duration = 460, delay = 0 } = {}): void {
  if (!el || reduce()) return;
  const ar = el.getBoundingClientRect();
  const fr = rectOf(fromEl);
  const c1 = center(ar);
  const c0 = fr ? center(fr) : { x: c1.x, y: c1.y + 40 };
  const s = fr ? Math.max(0.15, Math.min(1, fr.width / Math.max(1, ar.width))) : 0.6;
  el.style.zIndex = '30';
  el.animate([
    { transform: `translate(${c0.x - c1.x}px, ${c0.y - c1.y}px) scale(${s})`, opacity: 0.2 },
    { transform: 'translate(0,0) scale(1)', opacity: 1 },
  ], { duration, delay, easing: EASE, composite: 'add', fill: 'backwards' }).onfinish = () => { el.style.zIndex = ''; };
}

export function popIn(el: Element | null, { duration = 320, delay = 0 } = {}): void {
  if (!el || reduce()) return;
  el.animate([{ transform: 'scale(.6)', opacity: 0 }, { transform: 'scale(1.06)', opacity: 1, offset: 0.7 }, { transform: 'scale(1)', opacity: 1 }], { duration, delay, easing: EASE, composite: 'add', fill: 'backwards' });
}

/** Всплывающее число/текст над элементом. */
export function floatText(el: Element | null, text: string, cls = '', { delay = 0, dy = -56 } = {}): void {
  const L = layer();
  if (!L || !el) return;
  const r = el.getBoundingClientRect();
  if (!r.width) return;
  const s = document.createElement('div');
  s.className = 'fx-float ' + cls;
  s.textContent = text;
  Object.assign(s.style, { left: (r.left + r.width / 2) + 'px', top: (r.top + r.height / 2) + 'px' });
  L.appendChild(s);
  s.animate([
    { transform: 'translate(-50%,-50%) scale(.5)', opacity: 0 },
    { transform: 'translate(-50%,-70%) scale(1.15)', opacity: 1, offset: 0.2 },
    { transform: `translate(-50%, calc(-50% + ${dy}px)) scale(1)`, opacity: 1, offset: 0.7 },
    { transform: `translate(-50%, calc(-50% + ${dy - 20}px)) scale(.9)`, opacity: 0 },
  ], { duration: 1100, delay, easing: 'ease-out', fill: 'forwards' }).onfinish = () => s.remove();
}

export function shake(el: Element | null, { strength = 6, duration = 380, delay = 0 } = {}): void {
  if (!el || reduce()) return;
  el.animate([
    { transform: 'translate(0,0)' }, { transform: `translate(${-strength}px, ${strength / 2}px)` }, { transform: `translate(${strength}px, ${-strength / 2}px)` },
    { transform: `translate(${-strength * 0.6}px, 0)` }, { transform: `translate(${strength * 0.6}px, 0)` }, { transform: 'translate(0,0)' },
  ], { duration, delay, composite: 'add' });
}
export function flash(el: Element | null, color = '#ff4d5e', { delay = 0, duration = 500 } = {}): void {
  if (!el) return;
  el.animate([{ boxShadow: `0 0 0 0 ${color}`, filter: 'brightness(1)' }, { boxShadow: `0 0 0 6px ${color}88`, filter: 'brightness(1.5)', offset: 0.3 }, { boxShadow: `0 0 0 0 ${color}00`, filter: 'brightness(1)' }], { duration, delay });
}
export function screenShake({ strength = 5 } = {}): void {
  shake(document.getElementById('app'), { strength, duration: 300 });
}
export function animateBar(bar: Element | null, fromPct: number, toPct: number, { delay = 0, duration = 500 } = {}): void {
  if (!bar) return;
  bar.animate([{ width: fromPct + '%' }, { width: toPct + '%' }], { duration, delay, easing: EASE, fill: 'backwards' });
}

/** Заставка на весь экран (беспредел, смерть, ход) */
/** До какого момента (мс) на экране заставка: всплывающие ачивки ждут её конца, чтобы не накладываться */
let splashUntil = 0;

export function splash(html: string, cls = '', { delay = 0, duration = 1500 } = {}): void {
  const L = layer();
  if (!L) return;
  splashUntil = Math.max(splashUntil, Date.now() + delay + duration);
  const s = document.createElement('div');
  s.className = 'fx-splash ' + cls;
  s.innerHTML = html;
  L.appendChild(s);
  s.animate([
    { transform: 'translate(-50%,-50%) scale(.4) rotate(-6deg)', opacity: 0 },
    { transform: 'translate(-50%,-50%) scale(1.08) rotate(1deg)', opacity: 1, offset: 0.18 },
    { transform: 'translate(-50%,-50%) scale(1) rotate(0)', opacity: 1, offset: 0.75 },
    { transform: 'translate(-50%,-50%) scale(1.2)', opacity: 0 },
  ], { duration, delay, easing: 'ease-out', fill: 'forwards' }).onfinish = () => s.remove();
}

// ---------- ачивки: карточка «Ачивка получена!» ----------

/** Ачивка для всплывающей карточки: значок, название, условие; pips — ступени серии (●●○) */
export interface AchPopup {
  icon: string;
  name: string;
  text: string;
  pips?: string;
}

const ACH_HOLD = 2900;
const achQueue: AchPopup[] = [];
let achBusy = false;

/**
 * Новые ачивки — по очереди карточками сверху: значок влетает с вращением на фоне лучей, по карточке пробегает
 * блик, во все стороны — искры. Заставку (победа) не перекрывают — ждут её конца. Без анимаций (системная
 * настройка) — просто плавно появляются.
 */
export function achievements(list: readonly AchPopup[]): void {
  achQueue.push(...list);
  if (!achBusy) nextAch();
}

function nextAch(): void {
  const a = achQueue.shift();
  const L = layer();
  if (!a || !L) {
    achBusy = false;
    return;
  }
  achBusy = true;
  const wait = Math.max(0, splashUntil - Date.now());
  if (wait > 0) {
    achQueue.unshift(a);
    setTimeout(nextAch, wait + 120);
    return;
  }
  const el = document.createElement('div');
  el.className = 'fx-ach';
  el.innerHTML = `<div class="fxa-rays"></div><div class="fxa-badge"><span class="fxa-ic">${esc(a.icon)}</span></div>`
    + `<div class="fxa-body"><div class="fxa-cap">🏅 Ачивка получена!</div><div class="fxa-name">${esc(a.name)}</div>`
    + `${a.pips ? `<div class="fxa-pips">${esc(a.pips)}</div>` : ''}<div class="fxa-text">${esc(a.text)}</div></div><div class="fxa-shine"></div>`;
  L.appendChild(el);
  sfx('ach');
  const done = (): void => { el.remove(); setTimeout(nextAch, 180); };
  if (reduce()) {
    el.animate([{ opacity: 0 }, { opacity: 1, offset: 0.1 }, { opacity: 1, offset: 0.9 }, { opacity: 0 }], { duration: ACH_HOLD, fill: 'forwards' }).onfinish = done;
    return;
  }
  // плавность — по отрезкам (влёт, отскок, уход), а не на всю анимацию: иначе карточка начинает гаснуть сразу
  el.animate([
    { transform: 'translate(-50%, -140%) scale(.6)', opacity: 0, easing: EASE },
    { transform: 'translate(-50%, 6%) scale(1.06)', opacity: 1, offset: 0.12, easing: 'ease-out' },
    { transform: 'translate(-50%, 0) scale(1)', opacity: 1, offset: 0.2 },
    { transform: 'translate(-50%, 0) scale(1)', opacity: 1, offset: 0.86, easing: 'ease-in' },
    { transform: 'translate(-50%, -60%) scale(.92)', opacity: 0 },
  ], { duration: ACH_HOLD, fill: 'forwards' }).onfinish = done;
  const badge = el.querySelector('.fxa-badge');
  badge?.animate([
    { transform: 'scale(0) rotate(-200deg)' },
    { transform: 'scale(1.25) rotate(12deg)', offset: 0.6 },
    { transform: 'scale(1) rotate(0)' },
  ], { duration: 640, delay: 140, easing: 'cubic-bezier(.3,1.4,.5,1)', fill: 'backwards' });
  el.querySelector('.fxa-rays')?.animate([
    { transform: 'translate(-50%,-50%) rotate(0) scale(.3)', opacity: 0 },
    { transform: 'translate(-50%,-50%) rotate(60deg) scale(1)', opacity: 1, offset: 0.25 },
    { transform: 'translate(-50%,-50%) rotate(200deg) scale(1.1)', opacity: 0 },
  ], { duration: ACH_HOLD, delay: 120, easing: 'linear', fill: 'both' });
  el.querySelector('.fxa-shine')?.animate([{ transform: 'translateX(-130%) skewX(-20deg)' }, { transform: 'translateX(330%) skewX(-20deg)' }],
    { duration: 900, delay: 520, easing: 'ease-in-out', fill: 'both' });
  // искры: из-под значка во все стороны
  const b = badge?.getBoundingClientRect();
  if (!b) return;
  const c = center(b);
  const COLORS = ['#ffd23f', '#ff8a1f', '#fff3b0', '#c96bff', '#5fd68a'];
  for (let i = 0; i < 16; i++) {
    const s = document.createElement('div');
    s.className = 'fxa-spark';
    s.style.background = COLORS[i % COLORS.length]!;
    Object.assign(s.style, { left: c.x + 'px', top: c.y + 'px' });
    L.appendChild(s);
    const ang = (i / 16) * Math.PI * 2 + Math.random() * 0.4;
    const dist = 56 + Math.random() * 64;
    s.animate([
      { transform: 'translate(-50%,-50%) scale(1)', opacity: 1 },
      { transform: `translate(calc(-50% + ${Math.cos(ang) * dist}px), calc(-50% + ${Math.sin(ang) * dist}px)) scale(.2)`, opacity: 0 },
    ], { duration: 700 + Math.random() * 300, delay: 320, easing: 'cubic-bezier(.1,.7,.3,1)', fill: 'both' }).onfinish = () => s.remove();
  }
}

/** Стрела атаки от одного элемента к другому */
export function beam(fromEl: Element | null, toEl: Element | null, { color = '#ff4d5e', delay = 0, duration = 450 } = {}): void {
  const L = layer();
  if (!L || !fromEl || !toEl || reduce()) return;
  const a = center(fromEl.getBoundingClientRect());
  const b = center(toEl.getBoundingClientRect());
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const ang = Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI;
  const s = document.createElement('div');
  s.className = 'fx-beam';
  Object.assign(s.style, { left: a.x + 'px', top: a.y + 'px', width: len + 'px', transform: `rotate(${ang}deg)`, background: `linear-gradient(90deg, ${color}00, ${color}, #fff)` });
  L.appendChild(s);
  s.animate([{ clipPath: 'inset(0 100% 0 0)', opacity: 1 }, { clipPath: 'inset(0 0 0 0)', opacity: 1, offset: 0.5 }, { clipPath: 'inset(0 0 0 100%)', opacity: 0.6 }], { duration, delay, easing: 'ease-in-out', fill: 'forwards' }).onfinish = () => s.remove();
}

// ---------- проигрывание событий движка ----------

/** Где на экране находятся игроки, полоски жизней, колоды — чтобы анимации знали, куда лететь */
export interface FxCtx {
  me: PlayerId;
  before: Snap;
  after: Snap;
  avatar(pid: PlayerId): HTMLElement | null;
  bar(pid: PlayerId): HTMLElement | null;
  power: HTMLElement | null;
  deckEl(pid: PlayerId): HTMLElement | null;
  discardEl(pid: PlayerId): HTMLElement | null;
  trashEl: HTMLElement | null;
  playerName(pid: PlayerId): string;
}

/** Проигрывает события по очереди; возвращает общую длительность (мс). */
export function playEvents(events: readonly GameEvent[], ctx: FxCtx): number {
  let t = 0;
  const step = (ms: number): void => { t += ms; };
  const name = (pid: PlayerId | null | undefined): string => (pid == null ? '' : ctx.playerName(pid));
  const cardName = (cid: string | null | undefined): string => cardOrNull(cid)?.name ?? '';
  for (const e of events) {
    const av = 'pid' in e && e.pid != null ? ctx.avatar(e.pid) : null;
    switch (e.type) {
      case 'turn':
        setTimeout(() => sfx('turn'), t);
        notice(`▶ Ход: <b>${esc(name(e.pid))}</b>`, e.pid === ctx.me ? 'n-turn n-mine' : 'n-turn', null, { hold: 900, priority: e.pid === ctx.me ? 'high' : 'low' });
        step(300);
        break;
      case 'play': {
        setTimeout(() => sfx('play'), t);
        const el = ctx.after.get(`c:${e.iid}`);
        if (el && !ctx.before.has(`c:${e.iid}`)) arrive(el.el, e.from != null ? ctx.avatar(e.from) : ctx.avatar(e.pid), { delay: t });
        // розыгрыши в ленту не идут: из карт там всплывают только чужие покупки (см. 'buy'); остальное — в журнале
        step(e.pid !== ctx.me ? 420 : 120);
        break;
      }
      case 'activate': {
        setTimeout(() => sfx('activate'), t);
        const el = e.iid != null ? ctx.after.get(`c:${e.iid}`) : undefined;
        if (el) flash(el.el, '#ffd23f', { delay: t });
        step(200);
        break;
      }
      case 'power':
        setTimeout(() => sfx('power'), t);
        floatText(ctx.power, `+${e.n} ⚡`, 'f-power', { delay: t });
        if (ctx.power) flash(ctx.power, '#ffd23f', { delay: t, duration: 400 });
        step(140);
        break;
      case 'chips':
        setTimeout(() => sfx('chips'), t);
        floatText(av, `${e.n > 0 ? '+' : ''}${e.n} ▲`, e.n > 0 ? 'f-chips' : 'f-minus', { delay: t, dy: -40 });
        step(120);
        break;
      case 'marketChip': {
        // чипсина ложится на карту барахолки — всплывающий «+1 ▲» над самой картой
        const el = ctx.after.get(`c:${e.iid}`);
        if (el) floatText(el.el, '+1 ▲', 'f-chips', { delay: t, dy: -30 });
        step(80);
        break;
      }
      case 'attack': {
        setTimeout(() => sfx('attack'), t);
        const from = e.from != null ? ctx.avatar(e.from) : null;
        for (const tid of e.targets) {
          const to = ctx.avatar(tid);
          if (from && to && from !== to) beam(from, to, { delay: t, color: e.bespredel ? '#ff4d5e' : '#ff8a1f' });
          if (to) flash(to, '#ff8a1f', { delay: t + 200 });
        }
        step(300);
        break;
      }
      case 'defendPrompt':
        break;
      case 'defend':
        setTimeout(() => sfx('defend'), t);
        floatText(av, '🛡 защита', 'f-shield', { delay: t });
        if (av) flash(av, '#4fb3ff', { delay: t });
        step(500);
        break;
      case 'redirect':
        floatText(ctx.avatar(e.to), '↩ перенаправлено', 'f-shield', { delay: t });
        step(300);
        break;
      case 'damage': {
        setTimeout(() => sfx('damage'), t);
        floatText(av, `−${e.n}`, 'f-dmg', { delay: t });
        shake(av, { delay: t, strength: 7 });
        if (av) flash(av, '#ff4d5e', { delay: t });
        const bar = ctx.bar(e.pid);
        if (bar) {
          const max = Number(bar.dataset['max']) || 25;
          animateBar(bar, Math.min(100, (e.life + e.n) / max * 100), Math.max(0, e.life / max * 100), { delay: t });
        }
        if (e.pid === ctx.me) setTimeout(() => screenShake({ strength: 4 }), t);
        step(420);
        break;
      }
      case 'life': {
        setTimeout(() => sfx(e.delta > 0 ? 'heal' : 'damage'), t);
        floatText(av, `${e.delta > 0 ? '+' : ''}${e.delta} ❤`, e.delta > 0 ? 'f-heal' : 'f-minus', { delay: t });
        step(300);
        break;
      }
      case 'heal': {
        setTimeout(() => sfx('heal'), t);
        floatText(av, `+${e.n} ❤`, 'f-heal', { delay: t });
        const bar = ctx.bar(e.pid);
        if (bar) {
          const max = Number(bar.dataset['max']) || 25;
          animateBar(bar, Math.max(0, (e.life - e.n) / max * 100), Math.min(100, e.life / max * 100), { delay: t });
        }
        step(300);
        break;
      }
      case 'die':
        setTimeout(() => { sfx('die'); screenShake({ strength: 8 }); }, t);
        notice(`☠ <b>${esc(name(e.pid))}</b> подох${e.killer != null ? ` от руки ${esc(name(e.killer))}` : ''}`, 'n-die');
        step(600);
        break;
      case 'prize':
        setTimeout(() => sfx('prize'), t);
        floatText(av, '🏆 главный приз', 'f-prize', { delay: t });
        step(400);
        break;
      case 'ddk': {
        setTimeout(() => sfx('ddk'), t);
        const d = DB.ddk[e.tid];
        floatText(av, '💀 жетон дохлого колдуна', 'f-minus', { delay: t });
        notice(`💀 <b>${esc(name(e.pid))}</b>: жетон дохлого колдуна (${e.vp} ПО)${d && d.text ? `<div class="n-text">${esc(d.text)}</div>` : ''}`, 'n-ddk');
        step(600);
        break;
      }
      case 'loser':
        setTimeout(() => sfx('loser'), t);
        floatText(av, e.on ? '🐴 ЛОШАРА' : 'больше не лошара', e.on ? 'f-minus' : 'f-heal', { delay: t });
        step(500);
        break;
      case 'bespredel': {
        setTimeout(() => sfx('bespredel'), t);
        const d = cardOrNull(e.cid);
        notice(`🔥 <b>${e.mega ? 'МЕГА' : ''}БЕСПРЕДЕЛ!</b><div class="n-text">${esc(d ? d.text : '')}</div>`, 'n-bespredel');
        step(800);
        break;
      }
      case 'buy': {
        setTimeout(() => sfx('buy'), t);
        const b = ctx.before.get(`c:${e.iid}`) ?? (e.pile === 'chaos' ? ctx.before.get('c:chaos') : undefined);
        const dest = e.pid === ctx.me ? (ctx.discardEl(e.pid) ?? ctx.avatar(e.pid)) : ctx.avatar(e.pid);
        if (b && b.html && dest) flyClone(b.html, b.rect, dest, { delay: t });
        if (e.pid !== ctx.me) notice(`🛒 ${esc(name(e.pid))} покупает за ${e.cost}`, 'n-buy', e.cid, { priority: 'low', hold: 1000 });
        step(300);
        break;
      }
      case 'gain': {
        const b = ctx.before.get(`c:${e.iid}`);
        const dest = ctx.discardEl(e.pid) ?? ctx.avatar(e.pid);
        if (b && b.html && dest) flyClone(b.html, b.rect, dest, { delay: t });
        else if (e.from != null && dest) {
          // передача от другого игрока: карта летит от его плашки
          const d = cardOrNull(e.cid);
          const src = ctx.avatar(e.from);
          if (d && src) flyClone(cardHtml({ cid: d.id }, { cls: 'sm', zone: 'view', key: null }), src.getBoundingClientRect(), dest, { delay: t });
          floatText(ctx.avatar(e.pid), `${e.back ? '↩' : '🎁'} ${cardName(e.cid)}`, 'f-buy', { delay: t, dy: -40 });
        } else if (e.where !== 'play') floatText(ctx.avatar(e.pid), `+ ${cardName(e.cid)}`, 'f-buy', { delay: t, dy: -40 });
        step(220);
        break;
      }
      case 'move':
        step(80);
        break;
      case 'ddkLost':
        floatText(av, e.to != null ? '💀 → жетон отдан' : '💀 жетон уничтожен', 'f-heal', { delay: t });
        step(200);
        break;
      case 'trash': {
        const b = ctx.before.get(`c:${e.iid}`);
        if (b && b.html && ctx.trashEl) flyClone(b.html, b.rect, ctx.trashEl, { delay: t });
        step(150);
        break;
      }
      case 'discard': {
        let k = 0;
        for (const iid of e.iids) {
          const b = ctx.before.get(`c:${iid}`);
          const dest = ctx.discardEl(e.pid) ?? ctx.avatar(e.pid);
          if (b && b.html && dest) flyClone(b.html, b.rect, dest, { delay: t + k * 70 });
          k++;
        }
        if (e.pid !== ctx.me) floatText(ctx.avatar(e.pid), `сброс: ${e.cids.length}`, 'f-minus', { delay: t, dy: -40 });
        step(250);
        break;
      }
      case 'draw': {
        if (e.pid === ctx.me) {
          setTimeout(() => sfx(e.reshuffled ? 'shuffle' : 'draw'), t);
          let k = 0;
          for (const iid of e.iids) {
            const a = ctx.after.get(`c:${iid}`);
            if (a && !ctx.before.has(`c:${iid}`)) arrive(a.el, ctx.deckEl(e.pid), { delay: t + k * 60 });
            k++;
          }
          step(120 + e.iids.length * 40);
        } else {
          floatText(ctx.avatar(e.pid), `+${e.n} карт`, 'f-buy', { delay: t, dy: -36 });
          step(100);
        }
        break;
      }
      case 'endTurn': {
        // сыгранные карты уезжают в сброс
        let k = 0;
        for (const [key, b] of ctx.before) {
          if (!key.startsWith('c:') || ctx.after.has(key) || !b.html) continue;
          if (b.el.dataset['zone'] === 'table') {
            const dest = ctx.discardEl(e.pid) ?? ctx.avatar(e.pid);
            if (dest) flyClone(b.html, b.rect, dest, { delay: t + k * 50, duration: 420 });
            k++;
          }
        }
        step(200);
        break;
      }
      case 'note':
        notice(`ℹ ${e.pid != null ? `<b>${esc(name(e.pid))}</b>: ` : ''}${esc(e.text)}`, 'n-note', null, { priority: 'low' });
        step(300);
        break;
      case 'end':
        setTimeout(() => sfx('win'), t);
        break;
    }
  }
  return t;
}
